import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  JsonEditor,
  type JsonEditorValue,
} from '@web-config-app/core-react-ui';
import {
  getGenerateJsonLogicAssumptions,
  useGenerateJsonLogic,
  useAiAssistPromptsEditorStateStoreActions,
} from '@web-config-app/core-react';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { buildAgentChatMessage, isAiAssistEnabled } from '@web-config-app/core';
import { Box } from '@leagueplatform/genesis-core';
import type { ControlComponent } from '@web-config-app/entity-form';
import {
  EntityFormControl,
  useEntityDetailsProps,
} from '@web-config-app/entity-form';
import { JsonLogicInputTopBar } from './json-logic-input-top-bar.component';
import { JsonLogicInputTemplateSelect } from './json-logic-input-template-select.component';
import { JsonLogicInputWithTabs } from './json-logic-input-with-tabs.component';
import { RuleTemplateForm } from './rule-template-form/rule-template-form.component';
import { JsonLogicInputControlWrapper } from './json-logic-input-control-wrapper.component';
import { useRuleCombinationContext } from './rule-combination-provider/rule-combination-provider.component';
import { useJsonLogicControl } from './use-json-logic-control';
import { defaultRenderers } from '../../../renderers/default-renderers';

/**
 * The Rule Template Json Logic Control is rendered within the Rule Combination control, and is rendered for a single rule.
 */
const RuleTemplateJsonLogicInputControlContents = ({
  data,
  handleChange,
  path,
  enabled,
  id,
  schema,
}: {
  data: string;
  handleChange: (path: string, value: any) => void;
  path: string;
  enabled: boolean;
  id: string;
  schema: AnnotatedJsonSchema;
}) => {
  const { formatMessage } = useIntl();
  const { generateJsonLogic } = useGenerateJsonLogic();
  const [aiAssumptions, setAiAssumptions] = React.useState<string>();
  const [promptText, setPromptText] = React.useState('');
  const { entityRootData, formData } = useEntityDetailsProps();
  const { getAiAssistPrompt, setAiAssistPrompt, removeAiAssistPrompt } =
    useAiAssistPromptsEditorStateStoreActions();
  const isGeneratingRef = React.useRef(false);

  const ruleContext = useRuleCombinationContext();
  const aiAssist =
    ruleContext?.aiAssist ??
    schema?.['x-entity-control']?.options?.jsonLogic?.aiAssist;

  const arrayIndexString = path.split('.').pop();
  const arrayIndex = arrayIndexString
    ? (() => {
        const parsed = parseInt(arrayIndexString, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      })()
    : undefined;
  const {
    rulesTemplates,
    rulesType,
    jsonContent,
    jsonLogicFieldPath,
    selectedRuleTemplate,
    ruleTemplateFormData,
    handleOnJsonChange,
    handleRuleTemplateFormDataChange,
    handleTemplateChange,
  } = useJsonLogicControl({
    data,
    handleChange,
    path,
    schema,
    parentFieldPath: ruleContext?.combinationFieldPath,
    isArrayItem: ruleContext?.isArray,
    arrayIndex,
  });
  const enabledAiAssist =
    enabled &&
    !selectedRuleTemplate &&
    aiAssist &&
    isAiAssistEnabled(aiAssist.includeIf)
      ? aiAssist
      : undefined;

  // Seed the prompt text from the persisted store for the current field path
  React.useEffect(() => {
    const persisted = getAiAssistPrompt(jsonLogicFieldPath);
    setPromptText(persisted ?? '');
  }, [getAiAssistPrompt, jsonLogicFieldPath]);

  // Clears the persisted prompt and textarea when JSON is manually edited.
  // Skips clearing during AI generation since the editor's onChange fires
  // when the generated result is written to the editor value.
  const handleOnJsonChangeWithPromptClear = React.useCallback(
    (content: JsonEditorValue) => {
      handleOnJsonChange(content);
      if (!isGeneratingRef.current) {
        setPromptText('');
        removeAiAssistPrompt(jsonLogicFieldPath);
      }
    },
    [handleOnJsonChange, removeAiAssistPrompt, jsonLogicFieldPath],
  );

  let parsedData;

  try {
    parsedData = data && JSON.parse(data);
  } catch (error) {
    console.error('JsonLogicInputControl - Could not parse Data:', error);
  }

  const ruleTemplateTabId = `rule-template-${path}`;
  const jsonLogicTabId = `json-logic-${path}`;
  const ruleTemplateTab = {
    value: ruleTemplateTabId,
    label: formatMessage({ id: 'RULE_TEMPLATE' }),
    content: (
      <Box>
        <RuleTemplateForm
          renderers={defaultRenderers}
          selectedRuleTemplate={selectedRuleTemplate}
          ruleTemplateData={ruleTemplateFormData}
          onRuleTemplateDataChange={handleRuleTemplateFormDataChange}
          disabled={!enabled}
        />
      </Box>
    ),
  };

  const jsonEditorTab = {
    value: jsonLogicTabId,
    label: formatMessage({ id: 'JSON_LOGIC' }),
    content: (
      <JsonEditor
        readOnly={!!selectedRuleTemplate || !enabled}
        onChange={handleOnJsonChangeWithPromptClear}
        editorValue={jsonContent ?? {}}
        editorId={id}
        topBarContents={
          <JsonLogicInputTopBar
            showDataPoints={enabled && !selectedRuleTemplate}
            aiAssist={
              enabledAiAssist
                ? {
                    promptPlaceholder: enabledAiAssist.promptPlaceholder,
                    assumptionsText: aiAssumptions,
                    prompt: promptText,
                    onPromptChange: setPromptText,
                    onGenerate: async (prompt) => {
                      setAiAssumptions(undefined);
                      isGeneratingRef.current = true;

                      const message = buildAgentChatMessage({
                        userInput: prompt,
                        aiAssistOptions: enabledAiAssist,
                        entityRootData: entityRootData as Record<
                          string,
                          unknown
                        >,
                        formData: formData as
                          | Record<string, unknown>
                          | undefined,
                      });

                      try {
                        const generatedJsonLogic = await generateJsonLogic({
                          prompt: message,
                          agentName: enabledAiAssist.agentName,
                        });
                        setAiAssumptions(generatedJsonLogic.assumptions);
                        setAiAssistPrompt(jsonLogicFieldPath, prompt);
                        handleOnJsonChange({
                          json: undefined,
                          text: JSON.stringify(
                            generatedJsonLogic.rule ?? {},
                            null,
                            2,
                          ),
                        });
                      } catch (error) {
                        const assumptions =
                          getGenerateJsonLogicAssumptions(error);
                        if (assumptions) {
                          setAiAssumptions(assumptions);
                        }
                        throw error;
                      } finally {
                        isGeneratingRef.current = false;
                      }
                    },
                  }
                : undefined
            }
          />
        }
      />
    ),
  };

  const jsonLogicInputTabsData = [ruleTemplateTab, jsonEditorTab];

  const emptyParsedData = parsedData && !Object.keys(parsedData).length;
  const rulesTypeIsUnknown = rulesType === 'unknown';

  // If the rulesType is unknown, we only want to show the json logic tab.
  // This is because the rule template tab will not be able to display the rule template data.
  const jsonLogicTabs = rulesTypeIsUnknown
    ? [jsonEditorTab]
    : jsonLogicInputTabsData;

  // Select the default tab as:
  //  rule template when (we have a selected rule template or if the data returned is undefined or if the data returned is an empty object) AND if the rulesType is not "unknown"
  //  json logic when the data returned is not an empty object and doesn't fall under our rule templates id.
  const defaultTab =
    (selectedRuleTemplate || !parsedData || emptyParsedData) &&
    !rulesTypeIsUnknown
      ? ruleTemplateTabId
      : jsonLogicTabId;

  return (
    <Box
      css={{
        width: '100%',
        paddingX: '$one',
        border: 'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
        borderRadius: '$medium',
      }}
    >
      {!rulesTypeIsUnknown && (
        <JsonLogicInputTemplateSelect
          rulesTemplates={rulesTemplates}
          onChange={handleTemplateChange}
          selectedTemplate={selectedRuleTemplate?.id}
          templateLabel={formatMessage({ id: 'RULE_TEMPLATE' })}
          id={path}
          disabled={!enabled}
        />
      )}
      <JsonLogicInputWithTabs
        tabsData={jsonLogicTabs}
        defaultTab={defaultTab}
        ariaLabel={formatMessage({ id: 'RULE_VIEW_OPTIONS_ARIA_LABEL' })}
      />
    </Box>
  );
};

// RuleTemplateJsonLogicInputControl component
export const RuleTemplateJsonLogicInputControl: ControlComponent = (props) => (
  <EntityFormControl
    {...props}
    renderControl={RuleTemplateJsonLogicInputControlContents}
    wrapperComponent={JsonLogicInputControlWrapper}
  />
);
